import React, { useEffect, useState, useRef } from 'react';
import * as Yup from 'yup';
import { Formik } from 'formik';
import PropTypes from 'prop-types';
import { usePrevious, useEffectOnce } from 'react-use';

import LoginSidebar from 'components/consumer/LoginSidebar/LoginSidebar';
import IconArrowRightSimple from 'components/icons/iconArrowRightSimple';
import CtaButton from 'components/essentials/Cta/CtaButton';
import NewsletterOptIn from 'components/consumer/NewsletterOptIn'; // CA-246: API call needs to be ready
import FormikFirstErrorFieldFocusOnSubmit from 'components/essentials/FormikFirstErrorFieldFocusOnSubmit';
import { EMAIL_REGEX } from 'commons/constants';
import BriteverifyClient from 'commons/britverify-client';
import getStoreDetails from 'commons/getStoreDetails';
import useUserData from 'data-hooks/useUserData';
import StepTitle from '../StepTitle';
import SelectEmployee from '../../../../cart/EmployeeSelect';
import EmailField from './components/EmailField';
import CheckoutSubmitButton from '../CheckoutSubmitButton';

const INITIAL_VALUES = {
  email_address: '',
  newsletter_opt_in: true,
};

export const emailFormSchema = Yup.object().shape({
  email_address: Yup.string()
    .required('Email address is required')
    .matches(EMAIL_REGEX, 'Invalid email'),
});

const validateEmailForm = async ({ email_address }) => {
  const validate = await BriteverifyClient.emailLookup(email_address);
  const errors = {};
  if (!validate?.valid && validate?.message) {
    errors.email_address = validate?.message;
  }
  return errors;
};

const EmailForm = ({
  email_address,
  newsletter_opt_in,
  onSubmit,
  orderSummary,
  updateEmail,
  validateFormOnLoad,
}) => {
  const {
    isLoggedIn,
    email: userEmail,
    firstName: userFirstName,
  } = useUserData();

  const isLoggedInPreviousValue = usePrevious(isLoggedIn);
  const userEmailPreviousValue = usePrevious(userEmail);
  const email_addressPreviousValue = usePrevious(email_address);

  const [eventsFired, setEventsFired] = useState(false);
  const [selectEmployeeError, setSelectEmployeeError] = useState(null);
  const [selectedStoreEmployee, setSelectedStoreEmployee] = useState(null);
  const [showSignInModal, setShowSignInModal] = useState(false);

  const formRef = useRef();
  const emailInputRef = useRef();
  const employeeSelectRef = useRef();

  useEffectOnce(() => {
    if (validateFormOnLoad && formRef.current?.submitForm) {
      formRef.current.submitForm();
    }
  });

  useEffect(() => {
    if (
      isLoggedInPreviousValue &&
      (!email_address || userEmailPreviousValue !== email_addressPreviousValue)
    ) {
      updateEmail(userEmailPreviousValue);
    }

    if (!eventsFired && isLoggedInPreviousValue && orderSummary) {
      setEventsFired(true);
    }
  }, [
    email_address,
    email_addressPreviousValue,
    eventsFired,
    isLoggedInPreviousValue,
    orderSummary,
    updateEmail,
    userEmailPreviousValue,
  ]);

  const openSignInModal = () => {
    setShowSignInModal(true);
  };

  const hideSignInModal = () => {
    setShowSignInModal(false);
  };

  const onStoreEmployeeChange = emp => {
    if (emp) {
      setSelectedStoreEmployee(emp);
      setSelectEmployeeError(null);
    }
  };

  const submitEmail = values => {
    const storeDetails = getStoreDetails();
    if (storeDetails?.id && !selectedStoreEmployee) {
      setSelectEmployeeError('Please select an employee');
      if (employeeSelectRef?.current) {
        employeeSelectRef.current.focus();
      }
      return;
    }
    onSubmit({
      ...values,
      selectedStoreEmployee,
    });
  };

  const initialValues = {
    email_address: email_address || INITIAL_VALUES.email_address,
    newsletter_opt_in:
      newsletter_opt_in === false
        ? newsletter_opt_in
        : INITIAL_VALUES.newsletter_opt_in,
  };

  return (
    <Formik
      ref={formRef}
      enableReinitialize
      name="checkoutEmailForm"
      initialValues={initialValues}
      isInitialValid={emailFormSchema.isValidSync(initialValues)}
      validationSchema={emailFormSchema}
      validateOnChange={false}
      validate={validateEmailForm}
      onSubmit={submitEmail}
      render={({
        isValid,
        values,
        errors,
        touched,
        handleSubmit,
        handleBlur,
        handleChange,
        setFieldValue,
        submitCount,
      }) => (
        <form onSubmit={handleSubmit} data-xc="email-form" className="email">
          <StepTitle number="1">
            {isLoggedIn
              ? `Hi ${userFirstName}`
              : 'Hi there. Checkout as a guest.'}
          </StepTitle>

          {!isLoggedIn ? (
            <div className="text-base font-normal text-gray">
              Have a Joybird account?{' '}
              <CtaButton
                styling="link"
                hasArrow={false}
                color="brand"
                type="button"
                onClick={evt => {
                  evt.preventDefault();
                  openSignInModal();
                }}
              >
                Sign in
              </CtaButton>{' '}
              <IconArrowRightSimple
                width="5"
                height="9"
                className="ml-[3px] [&_path]:stroke-[#424242] inline relative -top-px"
              />
            </div>
          ) : null}

          <div className="mt-0 md:mt-6">
            <EmailField
              autoFocus
              label="Email *"
              disabled={isLoggedIn}
              readOnly={isLoggedIn}
              handleBlur={handleBlur}
              handleChange={handleChange}
              value={values.email_address}
              error={errors.email_address}
              isTouched={touched.email_address}
              refField={emailInputRef}
            />
            <NewsletterOptIn
              onClick={isChecked => {
                setFieldValue('newsletter_opt_in', isChecked);
              }}
              checked={values.newsletter_opt_in}
              value={values.newsletter_opt_in}
            />
            <div className="employee-select-container">
              <SelectEmployee
                errorMessage={selectEmployeeError}
                selectedEmployee={selectedStoreEmployee}
                employeeSelectRef={employeeSelectRef}
                onEmployeeChange={onStoreEmployeeChange}
              />
            </div>
            <div className="flex justify-end my-[30px]">
              <CheckoutSubmitButton
                dataXc="email-submit-btn"
                disabled={submitCount > 0 && !isValid}
              >
                Continue to shipping
              </CheckoutSubmitButton>
            </div>
          </div>
          <LoginSidebar
            onLogin={loginUser => {
              setFieldValue('email_address', loginUser.email);
              hideSignInModal();
            }}
            show={showSignInModal}
            onClose={hideSignInModal}
          />
          <FormikFirstErrorFieldFocusOnSubmit
            formFieldRefs={{
              email_address: emailInputRef,
            }}
          />
        </form>
      )}
    />
  );
};

EmailForm.propTypes = {
  email_address: PropTypes.string,
  newsletter_opt_in: PropTypes.bool,
  orderSummary: PropTypes.object,
  onSubmit: PropTypes.func,
  updateEmail: PropTypes.func,
  validateFormOnLoad: PropTypes.bool,
};

export default EmailForm;
